import { useQuery } from '@tanstack/react-query';
import { Activity, Database, Zap } from 'lucide-react';
import { invokeApiProxy } from '@/lib/apiProxy';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface HealthStatus {
  status: 'healthy' | 'unhealthy';
  fastapi: boolean;
  services?: {
    weaviate?: boolean;
    redis?: boolean;
    vertex_ai?: boolean;
  };
  timestamp: string;
}

interface HealthCheckProps {
  audience?: 'admin' | 'ops';
}

export function HealthCheck({ audience = 'admin' }: HealthCheckProps) {
  const { data: health, isLoading } = useQuery<HealthStatus>({
    queryKey: ['fastapi-health'],
    queryFn: async () => {
      try {
        const { data, error } = await invokeApiProxy<HealthStatus>({
          endpoint: '/api/v1/health/ready',
          method: 'GET',
        });

        if (error || !data) {
          console.error('Health check error:', error);
          return {
            status: 'unhealthy',
            fastapi: false,
            timestamp: new Date().toISOString()
          };
        }

        return {
          status: data.status === 'ready' ? 'healthy' : 'unhealthy',
          fastapi: data.status === 'ready',
          services: data.services,
          timestamp: new Date().toISOString()
        } as HealthStatus;
      } catch (err) {
        console.error('Health check failed:', err);
        return {
          status: 'unhealthy',
          fastapi: false,
          timestamp: new Date().toISOString()
        };
      }
    },
    refetchInterval: 30000, // Check every 30 seconds
    retry: 1,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <div className="w-2 h-2 rounded-full bg-muted animate-pulse" />
        <span className="text-xs">接続確認中...</span>
      </div>
    );
  }

  const isHealthy = health?.status === 'healthy' && health?.fastapi;
  const services = health?.services || {};
  const statusText = isHealthy ? '稼働中' : 'オフライン';
  const summaryText = audience === 'ops' ? `API: ${statusText}` : `FastAPI: ${statusText}`;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 text-sm cursor-pointer" data-audience={audience}>
            <div className={`w-2 h-2 rounded-full ${
              isHealthy ? 'bg-green-500' : 'bg-red-500'
            } ${isHealthy ? 'animate-pulse' : ''}`} />
            <span className="text-xs hidden sm:inline">
              {summaryText}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="p-3">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
              <Activity className={`w-3 h-3 ${isHealthy ? 'text-green-500' : 'text-red-500'}`} />
              <span>FastAPI: {isHealthy ? '✓' : '✗'}</span>
            </div>
            {services.weaviate !== undefined && (
              <div className="flex items-center gap-2 text-xs">
                <Database className={`w-3 h-3 ${services.weaviate ? 'text-green-500' : 'text-red-500'}`} />
                <span>Weaviate: {services.weaviate ? '✓' : '✗'}</span>
              </div>
            )}
            {services.redis !== undefined && (
              <div className="flex items-center gap-2 text-xs">
                <Zap className={`w-3 h-3 ${services.redis ? 'text-green-500' : 'text-red-500'}`} />
                <span>Redis: {services.redis ? '✓' : '✗'}</span>
              </div>
            )}
            {services.vertex_ai !== undefined && (
              <div className="flex items-center gap-2 text-xs">
                <Activity className={`w-3 h-3 ${services.vertex_ai ? 'text-green-500' : 'text-red-500'}`} />
                <span>Vertex AI: {services.vertex_ai ? '✓' : '✗'}</span>
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">
              最終確認: {new Date(health?.timestamp || '').toLocaleTimeString('ja-JP')}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
